import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react'

import * as api from '../api/tuk.api.js'
import { ApiError } from '../api/client.js'
import { DataTable, type Column } from '../components/DataTable.tsx'
import { useAuth } from '../auth/AuthProvider.tsx'

function pesan(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Tidak dapat menghubungi server'
  const detail = error.details?.map((d) => `${d.field}: ${d.message}`).join('; ')
  return detail !== undefined && detail !== '' ? detail : error.message
}

/** Cakupan nasional boleh membuat TUK dan menyatakan kelayakannya. */
function useNational(): boolean {
  const { user } = useAuth()
  return user?.roles.some((r) => r.scope === 'NASIONAL') ?? false
}

export function TukPage(): ReactNode {
  const [version, setVersion] = useState(0)
  const [dibuka, setDibuka] = useState<api.Tuk | null>(null)
  const nasional = useNational()

  const fetchPage = useCallback((page: number, q: string) => api.listTuk(page, q), [])

  const COLUMNS: readonly Column<api.Tuk>[] = [
    { key: 'kode', label: 'Kode', render: (t) => <code>{t.code}</code> },
    { key: 'nama', label: 'Nama', render: (t) => t.name },
    { key: 'region', label: 'Region', render: (t) => t.region.name },
    {
      key: 'isi',
      label: 'Ruang / Inventaris',
      render: (t) => `${t._count.rooms} ruang · ${t._count.inventory} item`,
    },
    {
      key: 'layak',
      label: 'Kelayakan',
      render: (t) =>
        t.isVerified ? (
          <span className="badge badge-diterbitkan">LAYAK</span>
        ) : (
          <span className="badge">BELUM DIPERIKSA</span>
        ),
    },
    { key: 'aksi', label: '', render: (t) => <button onClick={() => setDibuka(t)}>Buka</button> },
  ]

  if (dibuka !== null) {
    return (
      <TukDetail
        tuk={dibuka}
        nasional={nasional}
        onBack={() => {
          setDibuka(null)
          setVersion((v) => v + 1)
        }}
      />
    )
  }

  return (
    <div>
      <h1>TUK</h1>
      <p className="hint">
        Tempat Uji Kompetensi beserta ruang dan inventarisnya. Kelayakan dinyatakan panitia pusat
        setelah uji jaringan dan kelistrikan; pernyataannya masuk rantai audit.
      </p>

      {nasional && (
        <div className="page-actions">
          <TambahTuk onDone={() => setVersion((v) => v + 1)} />
        </div>
      )}

      <DataTable columns={COLUMNS} fetchPage={fetchPage} version={version} />
    </div>
  )
}

function TambahTuk({ onDone }: { onDone: () => void }): ReactNode {
  const [open, setOpen] = useState(false)
  const [regions, setRegions] = useState<readonly api.Region[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (open)
      api
        .listRegions()
        .then(setRegions)
        .catch(() => setRegions([]))
  }, [open])

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setBusy(true)
    setError(null)
    try {
      await api.createTuk({
        code: String(form.get('code') ?? ''),
        name: String(form.get('name') ?? ''),
        regionId: String(form.get('regionId') ?? ''),
        ...(form.get('address') ? { address: String(form.get('address')) } : {}),
      })
      setOpen(false)
      onDone()
    } catch (err) {
      setError(pesan(err))
    } finally {
      setBusy(false)
    }
  }

  if (!open) return <button onClick={() => setOpen(true)}>+ Tambah TUK</button>

  return (
    <form className="inline-form" onSubmit={onSubmit}>
      <input name="code" placeholder="Kode, mis. TUK-JKT-01" required />
      <input name="name" placeholder="Nama TUK" required />
      <select name="regionId" required defaultValue="">
        <option value="" disabled>
          Pilih region…
        </option>
        {regions.map((r) => (
          <option key={r.id} value={r.id}>
            {r.code} — {r.name}
          </option>
        ))}
      </select>
      <input name="address" placeholder="Alamat" />
      {error !== null && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <div className="inline-form-actions">
        <button type="submit" disabled={busy}>
          Simpan
        </button>
        <button type="button" onClick={() => setOpen(false)} disabled={busy}>
          Batal
        </button>
      </div>
    </form>
  )
}

function TukDetail({
  tuk,
  nasional,
  onBack,
}: {
  tuk: api.Tuk
  nasional: boolean
  onBack: () => void
}): ReactNode {
  const [rooms, setRooms] = useState<readonly api.Room[]>([])
  const [items, setItems] = useState<readonly api.InventoryItem[]>([])
  const [layak, setLayak] = useState(tuk.isVerified)
  const [error, setError] = useState<string | null>(null)

  const muat = useCallback(async () => {
    try {
      const [r, i] = await Promise.all([api.listRooms(tuk.id), api.listInventory(tuk.id)])
      setRooms(r)
      setItems(i)
      setError(null)
    } catch (err) {
      setError(pesan(err))
    }
  }, [tuk.id])

  useEffect(() => {
    void muat()
  }, [muat])

  return (
    <div>
      <button onClick={onBack}>← Kembali</button>
      <h1>{tuk.name}</h1>
      <p className="hint">
        <code>{tuk.code}</code> · {tuk.region.name}
        {tuk.address === null ? '' : ` · ${tuk.address}`}
      </p>

      {error !== null && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      <div className="page-actions">
        {layak ? (
          <span className="badge badge-diterbitkan">DINYATAKAN LAYAK</span>
        ) : (
          <span className="badge">BELUM DIPERIKSA</span>
        )}
        {nasional && (
          <button
            onClick={() => {
              api
                .setVerified(tuk.id, !layak)
                .then(() => setLayak(!layak))
                .catch((err: unknown) => setError(pesan(err)))
            }}
          >
            {layak ? 'Cabut kelayakan' : 'Nyatakan layak'}
          </button>
        )}
      </div>

      <div className="naskah-layout">
        <section>
          <h2>Ruang</h2>
          <TambahRuang tukId={tuk.id} onDone={() => void muat()} onError={setError} />
          {rooms.length === 0 ? (
            <p className="dt-empty">Belum ada ruang.</p>
          ) : (
            <ul className="ringkas-list">
              {rooms.map((r) => (
                <li key={r.id}>
                  <span>
                    <code>{r.code}</code> {r.name}{' '}
                    <span className="hint">kapasitas {r.capacity}</span>
                  </span>
                  <button
                    onClick={() => {
                      api
                        .deleteRoom(r.id)
                        .then(() => muat())
                        .catch((err: unknown) => setError(pesan(err)))
                    }}
                  >
                    Hapus
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h2>Inventaris</h2>
          <p className="hint">
            Menjadi pembanding kebutuhan station saat menyusun checklist kesiapan.
          </p>
          <TambahInventaris tukId={tuk.id} onDone={() => void muat()} onError={setError} />
          {items.length === 0 ? (
            <p className="dt-empty">Belum ada inventaris.</p>
          ) : (
            <ul className="ringkas-list">
              {items.map((i) => (
                <li key={i.id}>
                  <span>
                    {i.name}{' '}
                    <span className="hint">{i.category.replace(/_/g, ' ').toLowerCase()}</span> ×{' '}
                    {i.quantity} <span className="hint">{i.condition}</span>
                  </span>
                  <button
                    onClick={() => {
                      api
                        .deleteInventory(i.id)
                        .then(() => muat())
                        .catch((err: unknown) => setError(pesan(err)))
                    }}
                  >
                    Hapus
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}

function TambahRuang({
  tukId,
  onDone,
  onError,
}: {
  tukId: string
  onDone: () => void
  onError: (p: string) => void
}): ReactNode {
  const [open, setOpen] = useState(false)

  if (!open) return <button onClick={() => setOpen(true)}>+ Ruang</button>

  return (
    <form
      className="inline-form"
      onSubmit={(e) => {
        e.preventDefault()
        const form = new FormData(e.currentTarget)
        api
          .createRoom({
            tukId,
            code: String(form.get('code') ?? ''),
            name: String(form.get('name') ?? ''),
            capacity: Number(form.get('capacity') ?? 1),
          })
          .then(() => {
            setOpen(false)
            onDone()
          })
          .catch((err: unknown) => onError(pesan(err)))
      }}
    >
      <input name="code" placeholder="Kode ruang" required />
      <input name="name" placeholder="Nama ruang" required />
      <input name="capacity" type="number" min={1} defaultValue={1} />
      <div className="inline-form-actions">
        <button type="submit">Simpan</button>
        <button type="button" onClick={() => setOpen(false)}>
          Batal
        </button>
      </div>
    </form>
  )
}

function TambahInventaris({
  tukId,
  onDone,
  onError,
}: {
  tukId: string
  onDone: () => void
  onError: (p: string) => void
}): ReactNode {
  const [open, setOpen] = useState(false)

  if (!open) return <button onClick={() => setOpen(true)}>+ Item</button>

  return (
    <form
      className="inline-form"
      onSubmit={(e) => {
        e.preventDefault()
        const form = new FormData(e.currentTarget)
        api
          .createInventory({
            tukId,
            category: String(form.get('category') ?? 'ALAT') as api.InventoryCategory,
            name: String(form.get('name') ?? ''),
            quantity: Number(form.get('quantity') ?? 1),
            condition: String(form.get('condition') ?? 'BAIK'),
          })
          .then(() => {
            setOpen(false)
            onDone()
          })
          .catch((err: unknown) => onError(pesan(err)))
      }}
    >
      <input name="name" placeholder="Nama item, mis. Manekin RJP" required />
      <select name="category" defaultValue="ALAT">
        {api.INVENTORY_CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {c.replace(/_/g, ' ').toLowerCase()}
          </option>
        ))}
      </select>
      <input name="quantity" type="number" min={1} defaultValue={1} />
      <input name="condition" placeholder="Kondisi" defaultValue="BAIK" />
      <div className="inline-form-actions">
        <button type="submit">Simpan</button>
        <button type="button" onClick={() => setOpen(false)}>
          Batal
        </button>
      </div>
    </form>
  )
}
